import type { adminViews as AdminViewsNS } from '@cwos/api-client';

/**
 * Pure derivations for the Right Now screen's live-operations panel (Phase B
 * — owner directive: Right Now is the live operating command center, and it
 * is action-first, not KPI-first).
 */

/** "2h 12m" / "45m" — a live duration beside a person's name. */
export function durationLabel(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  if (h === 0) return `${m % 60}m`;
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}

/**
 * The workforce board's frame, said out loud. `admin-views:read` alone is a
 * caseload board; rendering its "2 on the clock" as an organization figure is
 * the flattening lie the board's own payload exists to prevent
 * (`WorkforceBoard.scope`'s docblock: read it before rendering
 * `currentlyClockedIn` as an organisation figure).
 */
export function boardScopeNote(scope: AdminViewsNS.BoardScope): string | null {
  return scope === 'caseload'
    ? 'Counts your caseload — you and your direct reports — not the whole organization.'
    : null;
}

/**
 * Today, as the [from, to) ISO window the schedule read takes — the reader's
 * own wall clock, which is the right frame for "what is happening now" on the
 * screen they are looking at.
 */
export function todayWindow(now: Date): { from: string; to: string } {
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * The clock exceptions the board carries, flattened to what the panel says:
 * how many, and the single worst case to name. Never invents an entry —
 * an empty board yields null, and the CALLER separates "empty because read"
 * from "empty because unread" (four-state doctrine).
 */
export function worstMissingClockOut(
  rows: readonly AdminViewsNS.MissingClockOutEntry[],
): AdminViewsNS.MissingClockOutEntry | null {
  if (rows.length === 0) return null;
  return [...rows].sort((a, b) => b.openDurationMinutes - a.openDurationMinutes)[0]!;
}
